import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../../../contexts/AuthContext";
import { useUser } from "../../../../contexts/UserContext";
import { updateProfile, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../../../firebase";

const ProfilePage: React.FC = () => {
  const { user: authUser } = useAuth();
  const { user, loading } = useUser();

  // Track if we've initialized the form
  const initializedRef = useRef(false);

  // Profile form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Password form state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Only initialize form values once when user data first loads
  useEffect(() => {
    if (user && !loading && !initializedRef.current) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setPhoneNumber(user.phoneNumber || "");
      initializedRef.current = true;
    }
  }, [user, loading]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!authUser) return;

    setIsUpdatingProfile(true);
    setProfileMessage(null);

    try {
      const displayName = `${firstName} ${lastName}`.trim();

      // Update Firebase Auth profile
      await updateProfile(authUser, { displayName });

      // Update Firestore user document
      const userRef = doc(db, "users", authUser.uid);
      await updateDoc(userRef, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: phoneNumber.trim(),
        updatedAt: new Date(),
      });

      setProfileMessage({ type: "success", text: "Profile updated successfully" });
      setTimeout(() => setProfileMessage(null), 4000);
    } catch (err: any) {
      setProfileMessage({ type: "error", text: err.message || "Failed to update profile" });
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!authUser || !authUser.email) return;

    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: "error", text: "Passwords do not match" });
      return;
    }

    if (newPassword.length < 8) {
      setPasswordMessage({ type: "error", text: "Password must be at least 8 characters" });
      return;
    }

    setIsUpdatingPassword(true);
    setPasswordMessage(null);

    try {
      // Re-authenticate user before password change
      const credential = EmailAuthProvider.credential(authUser.email, currentPassword);
      await reauthenticateWithCredential(authUser, credential);

      // Update password
      await updatePassword(authUser, newPassword);

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordMessage({ type: "success", text: "Password updated successfully" });
      setTimeout(() => setPasswordMessage(null), 4000);
    } catch (err: any) {
      if (err.code === "auth/wrong-password") {
        setPasswordMessage({ type: "error", text: "Current password is incorrect" });
      } else {
        setPasswordMessage({ type: "error", text: err.message || "Failed to update password" });
      }
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-gallery-300 border-t-gallery-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Profile Information */}
      <div className="bg-white border border-gallery-200 rounded-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gallery-100">
          <h2 className="font-display text-lg text-gallery-900">Profile Information</h2>
          <p className="text-sm text-gallery-500 mt-1">
            Update your personal details and contact information
          </p>
        </div>

        <form onSubmit={handleUpdateProfile} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gallery-700 mb-2">
                First Name
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gallery-200 rounded-sm text-gallery-900 placeholder-gallery-400 focus:outline-none focus:border-gallery-400 focus:ring-1 focus:ring-gallery-400 transition-colors"
                placeholder="Enter first name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gallery-700 mb-2">
                Last Name
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gallery-200 rounded-sm text-gallery-900 placeholder-gallery-400 focus:outline-none focus:border-gallery-400 focus:ring-1 focus:ring-gallery-400 transition-colors"
                placeholder="Enter last name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gallery-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={authUser?.email || ""}
                disabled
                className="w-full px-4 py-2.5 bg-gallery-50 border border-gallery-200 rounded-sm text-gallery-500 cursor-not-allowed"
              />
              <p className="text-xs text-gallery-400 mt-1.5">
                Contact support to change your email address
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gallery-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gallery-200 rounded-sm text-gallery-900 placeholder-gallery-400 focus:outline-none focus:border-gallery-400 focus:ring-1 focus:ring-gallery-400 transition-colors"
                placeholder="+1 (555) 000-0000"
              />
            </div>
          </div>

          {profileMessage && (
            <div
              className={`mt-6 px-4 py-3 rounded-sm text-sm ${
                profileMessage.type === "success"
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-rose-50 text-rose-700 border border-rose-200"
              }`}
            >
              {profileMessage.text}
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              disabled={isUpdatingProfile}
              className="px-6 py-2.5 bg-gallery-900 text-white text-sm font-medium rounded-sm hover:bg-gallery-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isUpdatingProfile ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>

      {/* Change Password */}
      <div className="bg-white border border-gallery-200 rounded-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gallery-100">
          <h2 className="font-display text-lg text-gallery-900">Change Password</h2>
          <p className="text-sm text-gallery-500 mt-1">
            Update your password to keep your account secure
          </p>
        </div>

        <form onSubmit={handleUpdatePassword} className="p-6">
          <div className="space-y-6 max-w-md">
            <div>
              <label className="block text-sm font-medium text-gallery-700 mb-2">
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gallery-200 rounded-sm text-gallery-900 placeholder-gallery-400 focus:outline-none focus:border-gallery-400 focus:ring-1 focus:ring-gallery-400 transition-colors"
                placeholder="Enter current password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gallery-700 mb-2">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gallery-200 rounded-sm text-gallery-900 placeholder-gallery-400 focus:outline-none focus:border-gallery-400 focus:ring-1 focus:ring-gallery-400 transition-colors"
                placeholder="Enter new password"
              />
              <p className="text-xs text-gallery-400 mt-1.5">
                Minimum 8 characters
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gallery-700 mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gallery-200 rounded-sm text-gallery-900 placeholder-gallery-400 focus:outline-none focus:border-gallery-400 focus:ring-1 focus:ring-gallery-400 transition-colors"
                placeholder="Confirm new password"
              />
            </div>
          </div>

          {passwordMessage && (
            <div
              className={`mt-6 px-4 py-3 rounded-sm text-sm max-w-md ${
                passwordMessage.type === "success"
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-rose-50 text-rose-700 border border-rose-200"
              }`}
            >
              {passwordMessage.text}
            </div>
          )}

          <div className="mt-6">
            <button
              type="submit"
              disabled={isUpdatingPassword || !currentPassword || !newPassword || !confirmPassword}
              className="px-6 py-2.5 bg-gallery-900 text-white text-sm font-medium rounded-sm hover:bg-gallery-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isUpdatingPassword ? "Updating..." : "Update Password"}
            </button>
          </div>
        </form>
      </div>

      {/* Account Details */}
      <div className="bg-white border border-gallery-200 rounded-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gallery-100">
          <h2 className="font-display text-lg text-gallery-900">Account Details</h2>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-gallery-500 mb-1">
                Account ID
              </p>
              <p className="text-sm font-mono text-gallery-700 truncate">
                {authUser?.uid}
              </p>
            </div>

            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-gallery-500 mb-1">
                Member Since
              </p>
              <p className="text-sm text-gallery-700">
                {user?.createdAt
                  ? new Date(user.createdAt.toDate()).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })
                  : "—"}
              </p>
            </div>

            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-gallery-500 mb-1">
                Last Updated
              </p>
              <p className="text-sm text-gallery-700">
                {user?.updatedAt
                  ? new Date(user.updatedAt.toDate()).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })
                  : "—"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
